import React, { FunctionComponent, useEffect, useState } from "react";
import { useI18n } from "../../../hooks/useI18n";
import { getProductSalesStatistics } from "../../../api/statisticsApi";
import { Currency } from "../../../components/misc/Currency";
import { ProductSaleStatistic } from "../../../models/Statistics";
import { isOk } from "../../../result";
import StatTable from "../components/StatTable";

export interface ProductSalesStatisticsProps {
  dateRange: any[];
  productSales: any;
  storeIds: any[];
  onLoad: (columns: any, data: any) => void;
}

export const ProductSalesStatistics: FunctionComponent<
  ProductSalesStatisticsProps
> = ({ dateRange, storeIds, onLoad, productSales }) => {
  const [loading, setLoading] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    if (dateRange && !productSales.data) {
      setLoading(true);
      getProductSalesStatistics(dateRange[0], dateRange[1], storeIds).then(
        response => {
          if (isOk(response)) {
            const [columns, data] = populateProductSales([...response.value]);
            onLoad(columns, data);
            setLoading(false);
          }
        }
      );
    }
  }, [dateRange, productSales, storeIds]);

  const populateProductSales = (
    productSales: ProductSaleStatistic[]
  ): [any, any] => {
    let columns = [
      {
        title: "GTIN",
        field: "gtin"
      },
      {
        title: "Product",
        field: "productName"
      },
      {
        title: "Brand",
        field: "brandName"
      },
      {
        title: t("common.tableHeaders.totalSales"),
        field: "amountSoldTotalFormatted",
        hidden: true,
        export: true
      },
      {
        title: "Total purchases / period",
        field: "quantitySoldTotal",
        hidden: true,
        export: true
      },
      {
        title: t("common.tableHeaders.totalSales"),
        field: "amountSoldInTable",
        export: false,
        cellStyle: { whiteSpace: "nowrap" },
        customSort: (a: any, b: any) => {
          return a.amountSoldTotal - b.amountSoldTotal;
        }
      }
    ];

    let data = [];
    let isPushed = [] as any;
    for (let productSale of productSales) {
      let dataEntry = {};
      for (let i = 0; i < productSale.salesStores.length; i++) {
        if (!isPushed.includes(productSale.salesStores[i].storeName)) {
          columns.push({
            title: `${productSale.salesStores[i].storeName} sales / period`,
            field: `${productSale.salesStores[i].storeName}amountSoldExclVatFormatted`,
            hidden: true,
            export: true
          });
          columns.push({
            title: `${productSale.salesStores[i].storeName} purchases / period`,
            field: `${productSale.salesStores[i].storeName}quantitySold`,
            hidden: true,
            export: true
          });
          columns.push({
            title: `${productSale.salesStores[i].storeName} sales`,
            field: `${productSale.salesStores[i].storeName}amountSoldInTable`,
            export: false,
            cellStyle: { whiteSpace: "nowrap" },
            customSort: (a: any, b: any) => {
              const direction = a.tableData.id > b.tableData.id;
              const a1 =
                a[`${productSale.salesStores[i].storeName}amountSoldExclVat`];
              const b1 =
                b[`${productSale.salesStores[i].storeName}amountSoldExclVat`];

              if (a1 === undefined && b1 === undefined) return 0;
              else if (a1 === undefined) return direction ? 1 : -1;
              else if (b1 === undefined) return direction ? -1 : 1;

              return b1 - a1;
            }
          });
          isPushed.push(productSale.salesStores[i].storeName);
        }
        dataEntry = {
          ...dataEntry,
          [`${productSale.salesStores[i].storeName}amountSoldExclVat`]:
            productSale.salesStores[i].amountSoldExclVat,
          [`${productSale.salesStores[i].storeName}amountSoldExclVatFormatted`]:
            productSale.salesStores[i].amountSoldExclVat,
          [`${productSale.salesStores[i].storeName}quantitySold`]:
            productSale.salesStores[i].quantitySold,
          [`${productSale.salesStores[i].storeName}amountSoldInTable`]: (
            <Currency value={productSale.salesStores[i].amountSoldExclVat}>
              ({productSale.salesStores[i].quantitySold}st)
            </Currency>
          )
        };
      }

      data.push({
        ...dataEntry,
        gtin: productSale.gtin,
        productName: productSale.productName,
        brandName: productSale.brandName,
        amountSoldTotalFormatted: productSale.amountSoldTotal,
        amountSoldTotal: productSale.amountSoldTotal,
        quantitySoldTotal: productSale.quantitySoldTotal,
        amountSoldInTable: (
          <Currency value={productSale.amountSoldTotal}>
            ({productSale.quantitySoldTotal}st)
          </Currency>
        )
      });
    }

    if (isPushed.length <= 1) columns.splice(-3, 3);

    return [columns, data];
  };

  return (
    <StatTable
      exportName={t("common.statistics.productSales")}
      isLoading={loading}
      columns={productSales.columns}
      data={productSales.data ?? []}
    />
  );
};
